'use client';

import type { CompanyRevenueHistoryPoint } from '@/scripts/company-data';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SourceAttribution } from './SourceAttribution';
import { createFormatters } from './currencyFormatters';

/**
 * Server-renderable data table mirroring CompanyTrendChart's data
 * (revenue / profit / balance sum / employees by year). Plain markup so the
 * figures land in the SSR HTML and are crawlable, unlike the recharts canvas
 * which is `ssr: false`. Rendered behind the Chart|Table toggle; the grid
 * header leaves its right edge clear for the toggle.
 */
export function FinancialHistoryTable({
  revenueHistory,
  companyName,
}: {
  revenueHistory: CompanyRevenueHistoryPoint[];
  companyName?: string;
}) {
  const { t, i18n } = useTranslation();
  const fmt = createFormatters(i18n.language);

  const rows = revenueHistory
    .filter(
      (r) =>
        r.totalRevenue != null ||
        r.grossProfit != null ||
        r.profit != null ||
        r.totalBalanceSum != null ||
        r.totalEmployees != null,
    )
    .slice()
    .sort((a, b) => b.year - a.year)
    // Show at most the 10 most recent years.
    .slice(0, 10);

  if (rows.length === 0) return null;

  const cell = (value: number | null) =>
    value == null ? '—' : fmt.fullCurrency.format(value);

  /**
   * The revenue cell, for the ~761k companies that publish a balance sheet
   * and no P&L. Withholding the P&L is a § 326 Abs. 1 relief only kleine
   * Kapitalgesellschaften may claim, so the blank cell was never "unknown" —
   * it was an unread size-class assertion.
   *
   * Four renderings, in descending strength, and the lower three must stay
   * visually distinct from a reported turnover:
   *   - a reported turnover        → the number, as before
   *   - a Rohergebnis (§ 276)      → the number, *labelled*
   *   - an entailed ceiling        → "≤ 15 Mio. €"
   *   - a class with no ceiling    → the class name
   *
   * Rohergebnis outranks the ceiling because it is a figure the company
   * actually published, but it is emphatically **not** revenue: it is already
   * net of material costs, and writing it into this column unlabelled is the
   * precise conflation this feature exists to undo — `financial_agg.revenue`
   * held it as "revenue" for a third of the corpus. So it carries its own
   * name in the cell, not merely a footnote marker, because a reader scanning
   * the column must not be able to mistake it for turnover.
   * A ceiling is only ever shown when § 267's two-of-three test *entails* it.
   * There is no soft ceiling: among companies that do report turnover, 24.3%
   * of those with a Bilanzsumme under 6 M€ are above 12 M€.
   */
  const revenueCell = (r: CompanyRevenueHistoryPoint) => {
    if (r.totalRevenue != null) return fmt.fullCurrency.format(r.totalRevenue);
    if (r.grossProfit != null) {
      return (
        <span className="font-medium text-foreground/80">
          <span className="mr-1 font-normal text-muted-foreground">
            {t('companyDetail.sizeClass.grossProfitLabel', {
              defaultValue: 'Rohergebnis',
            })}
          </span>
          {fmt.compactCurrency.format(r.grossProfit)}
          <sup className="ml-0.5">2</sup>
        </span>
      );
    }
    if (r.revenueCeiling != null) {
      return (
        <span className="font-medium text-foreground/80">
          {t('companyDetail.sizeClass.atMost', {
            value: fmt.compactCurrency.format(r.revenueCeiling),
            defaultValue: '≤ {{value}}',
          })}
          <sup className="ml-0.5">1</sup>
        </span>
      );
    }
    if (r.sizeClass) {
      return (
        <span className="italic text-muted-foreground">
          {t(`companyDetail.sizeClass.short.${r.sizeClass}`, {
            defaultValue: r.sizeClass,
          })}
          <sup className="ml-0.5">1</sup>
        </span>
      );
    }
    return '—';
  };

  // One footnote for the whole table, not a tooltip per row — the legal basis
  // is the same for every marked cell.
  const showFootnote = rows.some(
    (r) => r.totalRevenue == null && (r.revenueCeiling != null || r.sizeClass),
  );
  const showGrossProfitFootnote = rows.some(
    (r) => r.totalRevenue == null && r.grossProfit != null,
  );

  return (
    <Card variant="grid">
      <CardHeader>
        <CardTitle as="h2">{t('companyDetail.trends.title')}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto pb-3">
        <table className="w-full border-collapse text-xs">
          <caption className="sr-only">
            {t('companyDetail.trends.tableCaption')}
            {companyName ? ` — ${companyName}` : ''}
          </caption>
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th scope="col" className="py-1.5 pr-3 font-semibold">
                {t('companyDetail.trends.year')}
              </th>
              <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                {t('companyDetail.trends.revenue')}
              </th>
              <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                {t('companyDetail.trends.profit')}
              </th>
              <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                {t('companyDetail.trends.balance')}
              </th>
              <th scope="col" className="py-1.5 text-right font-semibold">
                {t('companyDetail.trends.employees')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.year} className="border-b border-border/50">
                <th
                  scope="row"
                  className="py-1.5 pr-3 font-medium text-foreground"
                >
                  {r.year}
                </th>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {revenueCell(r)}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {cell(r.profit)}
                </td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {cell(r.totalBalanceSum)}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {r.totalEmployees == null
                    ? '—'
                    : fmt.fullNumber.format(r.totalEmployees)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {showFootnote && (
          <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
            <sup>1</sup>{' '}
            {t('companyDetail.sizeClass.footnote', {
              defaultValue:
                'Kleine Kapitalgesellschaften müssen keine Gewinn- und Verlustrechnung offenlegen (§ 326 Abs. 1 HGB). Angegeben ist die gesetzliche Obergrenze nach § 267 HGB, soweit sie sich aus der Bilanzsumme zwingend ergibt.',
            })}
          </p>
        )}
        {showGrossProfitFootnote && (
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            <sup>2</sup>{' '}
            {t('companyDetail.sizeClass.grossProfitFootnote', {
              defaultValue:
                'Rohergebnis nach § 276 HGB: Umsatzerlöse abzüglich Materialaufwand bzw. Herstellungskosten. Kleine und mittelgroße Kapitalgesellschaften dürfen diese Posten zusammenfassen; die Umsatzerlöse selbst sind dann nicht veröffentlicht und liegen über dem Rohergebnis.',
            })}
          </p>
        )}
        <SourceAttribution source="unternehmensregister" />
      </CardContent>
    </Card>
  );
}
